import clsx from "clsx"
import { memo, useMemo } from "react"

import useNavigationLinks, { NavigationLink } from "@hooks/useNavigationLinks"
import useUser from "@hooks/useUser"

import filterUserLinks from "@utils/filterUserLinks"
import generateRandomKey from "@utils/generateRandomKey"

import AccessCheck from "@particles/AccessCheck/AccessCheck"

import { ScrollableContainer } from "@atoms"

import NavSidebarItem from "@organisms/NavSidebar/components/NavSidebarItem/NavSidebarItem"
import NavSidebarItemGroup from "@organisms/NavSidebar/components/NavSidebarItemGroup/NavSidebarItemGroup"
import NavSidebarProfileButton from "@organisms/NavSidebar/components/NavSidebarProfileButton/NavSidebarProfileButton"
import NavSidebarWorkspaceButton from "@organisms/NavSidebar/components/NavSidebarWorkspaceButton/NavSidebarWorkspaceButton"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./NavSidebar.module.scss"
import { NavSidebarProps } from "./NavSidebar.types"
import HelpCenter from "./components/HelpCenter/HelpCenter"
import NavSidebarCreateButton from "./components/NavSidebarCreateButton/NavSidebarCreateButton"
import NavSidebarSearchButton from "./components/NavSidebarSearchButton/NavSidebarSearchButton"
import WhatsNew from "./components/WhatsNew/WhatsNew"

function NavSidebar(props: NavSidebarProps) {
    const { hideCreateButton = false, hideSearchButton = false } = props
    const { user } = useUser()

    const { home, calendar, estimates, jobs, invoices, purchase_orders, bills, vendors, clients, pricebook, reports } =
        useNavigationLinks()

    const links: NavigationLink[] = [
        home,
        calendar,
        estimates,
        jobs,
        invoices,
        purchase_orders,
        bills,
        vendors,
        clients,
        pricebook,
        reports,
    ]

    const filteredLinks = useMemo(() => filterUserLinks(links, user), [links, user])

    return (
        <nav className={clsx(styles.base, "jsNavSidebar")}>
            <div className={styles.header}>
                <NavSidebarWorkspaceButton />

                <NavSidebarProfileButton />
            </div>
            <ScrollableContainer className={clsx(styles.sideBarItems)}>
                <div className={styles.topItems}>
                    {!hideCreateButton && (
                        <AccessCheck
                            permissions={{
                                estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                jobs_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                purchase_orders_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                bills_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                vendors_create_permission: PERMISSION_LEVEL.FULL,
                                clients_create_permission: PERMISSION_LEVEL.FULL,
                                pricebook_create_permission: PERMISSION_LEVEL.FULL,
                            }}
                        >
                            <NavSidebarCreateButton />
                        </AccessCheck>
                    )}
                    <div className={styles.primaryNavigation}>
                        {!hideSearchButton && (
                            <AccessCheck
                                permissions={{
                                    estimates_list_permission: PERMISSION_LEVEL.FULL,
                                    jobs_list_permission: PERMISSION_LEVEL.FULL,
                                    invoices_list_permission: PERMISSION_LEVEL.FULL,
                                    purchase_orders_list_permission: PERMISSION_LEVEL.FULL,
                                    bills_list_permission: PERMISSION_LEVEL.FULL,
                                    vendors_list_permission: PERMISSION_LEVEL.FULL,
                                    clients_list_permission: PERMISSION_LEVEL.FULL,
                                    pricebook_list_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <NavSidebarSearchButton />
                            </AccessCheck>
                        )}
                        {filteredLinks.map((link) => {
                            const sublinkCount = filterUserLinks(link.subLinks, user).length
                            if (sublinkCount > 1) {
                                return (
                                    <NavSidebarItemGroup
                                        key={generateRandomKey()}
                                        header={link}
                                        items={filterUserLinks(link.subLinks, user)}
                                    />
                                )
                            } else if (sublinkCount === 1) {
                                const sublink = filterUserLinks(link.subLinks, user)[0]
                                return (
                                    <NavSidebarItem
                                        key={generateRandomKey()}
                                        label={sublink.label}
                                        path={sublink.path}
                                        icon={link.icon}
                                        iconActive={link.iconActive}
                                    />
                                )
                            } else {
                                return (
                                    <NavSidebarItem
                                        key={generateRandomKey()}
                                        label={link.label}
                                        path={link.path}
                                        icon={link.icon}
                                        iconActive={link.iconActive}
                                    />
                                )
                            }
                        })}
                    </div>
                </div>
                <div className={styles.secondaryNavigationItems}>
                    <HelpCenter />
                    <WhatsNew />
                </div>
            </ScrollableContainer>
        </nav>
    )
}

export default memo(NavSidebar)
